import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import set from 'lodash/set';
import styled from 'styled-components';

import { CustomRow } from 'src/presentation/components/forms/CustomRow';
import DatePicker from 'src/presentation/components/forms/DatePicker';
import FormContext from 'src/presentation/contexts/FormContext';
import {
  StartStopSelectorsWrapper,
  TimeSelector,
  ToSeparator,
} from 'src/presentation/components/pages/shared/HoursTable/TimeRange';

type DateTimeRangeType = {
  enableEndDate?: boolean;
};

const dateLabel = 'Date';
const timeLabel = 'Time';

const DateTimeRange = ({ enableEndDate }: DateTimeRangeType) => {
  const currentDate = moment();
  const currentDateStr = currentDate.format('MM/DD/YYYY');
  const currentTimeStr = currentDate.format('HH:mm');
  const defaultEndBeforeDateStr = currentDate.add(1, 'day').format('MM/DD/YYYY');

  const [endDateEnabled, setEndDateEnabled] = useState(enableEndDate);

  const [selectedStartAfterDate, setSelectedStartAfterDate] = useState(currentDateStr);
  const [selectedStartAfterTime, setSelectedStartAfterTime] = useState(currentTimeStr);
  const [selectedEndBeforeDate, setSelectedEndBeforeDate] = useState(defaultEndBeforeDateStr);
  const [selectedEndBeforeTime, setSelectedEndBeforeTime] = useState(currentTimeStr);

  const { updateState } = useContext(FormContext);

  useEffect(() => {
    setEndDateEnabled(enableEndDate);
  }, [enableEndDate, setEndDateEnabled]);

  const onTimeChange = ({ attr, value }) => {
    updateState(set({}, attr, value));
    if (attr === 'endBeforeTime') {
      setSelectedEndBeforeTime(value);
      return;
    }

    if (attr === 'startAfterTime') {
      setSelectedStartAfterTime(value);
      return;
    }
  };

  const onStartDateChange = formattedDate => {
    updateState({ startAfterDate: formattedDate });
    setSelectedStartAfterDate(formattedDate);
    return;
  };

  const onEndDateChange = formattedDate => {
    updateState({ endBeforeDate: formattedDate });
    setSelectedEndBeforeDate(formattedDate);
    return;
  };

  return (
    <StartEndDateRow endDateEnabled={endDateEnabled}>
      <StyledStartStopSelectorsWrapper>
        <StyledDatePicker
          attr="startAfterDate"
          onChange={onStartDateChange}
          value={selectedStartAfterDate}
          label={dateLabel}
        />
        <StyledTimeSelector
          attr="startAfterTime"
          fullAttr="startAfter"
          intervalPart={0}
          height={`33px`}
          value={selectedStartAfterTime}
          onChange={onTimeChange}
          label={timeLabel}
        />
        {endDateEnabled && (
          <>
            <ToSeparator>to</ToSeparator>
            <StyledDatePicker
              attr="endBeforeDate"
              onChange={onEndDateChange}
              value={selectedEndBeforeDate}
              label={dateLabel}
            />
            <StyledTimeSelector
              attr="endBeforeTime"
              fullAttr="endBefore"
              intervalPart={0}
              value={selectedEndBeforeTime}
              onChange={onTimeChange}
              label={timeLabel}
            />
          </>
        )}
      </StyledStartStopSelectorsWrapper>
    </StartEndDateRow>
  );
};

const StartEndDateRow = styled(CustomRow)`
  width: 100%;
`;

const StyledStartStopSelectorsWrapper = styled(StartStopSelectorsWrapper)`
  gap: 8px;
  align-items: flex-end;
`;

const StyledDatePicker = styled(DatePicker)`
  max-width: 97px;
`;

const StyledTimeSelector = styled(TimeSelector)`
  padding-top: 0px;
  height: 32px;
  & .rc-time-picker-input {
    height: 32px;
    border: 1px solid ${p => p.theme.colors.gray300};
    ::placeholder {
      color: ${p => p.theme.colors.gray500};
    }
  }
`;

export default DateTimeRange;
